import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, RotateCcw, ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";
import { api, extractError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

// Human-friendly role labels for the column headers.
const ROLE_LABEL = { admin: "Admin", accounting: "Accounting", worker: "Worker" };

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

// Deterministic set equality for role-perm lists (backend returns sorted arrays).
function sameSet(a, b) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

// Compute a matrix-level diff count vs the server-side snapshot.
function countPendingChanges(current, original) {
  let n = 0;
  const roles = new Set([...Object.keys(current), ...Object.keys(original)]);
  roles.forEach((r) => {
    const cur = new Set(current[r] || []);
    const orig = new Set(original[r] || []);
    cur.forEach((k) => { if (!orig.has(k)) n += 1; });
    orig.forEach((k) => { if (!cur.has(k)) n += 1; });
  });
  return n;
}

export default function PermissionsMatrix() {
  const { user, hasPerm, loading: authLoading, loadPermissions } = useAuth();

  const [payload, setPayload] = useState(null);   // {matrix, all_keys, roles, locked_keys_for_admin, updated_at, updated_by}
  const [draft, setDraft] = useState(null);        // editable copy of `matrix`
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: matrixResp }, { data: auditResp }] = await Promise.all([
        api.get("/admin/permissions"),
        api.get("/admin/permissions/audit", { params: { limit: 50 } }),
      ]);
      setPayload(matrixResp);
      // Deep clone matrix into an editable draft (arrays only).
      const cloned = {};
      Object.entries(matrixResp.matrix || {}).forEach(([r, ks]) => {
        cloned[r] = [...ks];
      });
      setDraft(cloned);
      setAudit(auditResp.entries || []);
      // Default all groups open first time.
      if (Object.keys(openGroups).length === 0) {
        const g = {};
        (matrixResp.all_keys || []).forEach((k) => { g[k.group] = true; });
        setOpenGroups(g);
      }
    } catch (e) {
      toast.error(extractError(e, "Failed to load permissions"));
    } finally {
      setLoading(false);
    }
  }, [openGroups]);

  useEffect(() => { if (!authLoading && user) load(); }, [authLoading, user, load]);

  // ---- Guardrails / helpers ----
  const lockedForAdmin = useMemo(
    () => new Set(payload?.locked_keys_for_admin || []),
    [payload],
  );

  const groupedKeys = useMemo(() => {
    const groups = {};
    (payload?.all_keys || []).forEach((k) => {
      groups[k.group] = groups[k.group] || [];
      groups[k.group].push(k);
    });
    return groups;
  }, [payload]);

  const pending = useMemo(
    () => (draft && payload ? countPendingChanges(draft, payload.matrix) : 0),
    [draft, payload],
  );

  const toggleCell = (role, key, on) => {
    // Guardrail: block toggling a locked admin key.
    if (role === "admin" && lockedForAdmin.has(key)) return;
    setDraft((cur) => {
      const next = { ...cur, [role]: [...(cur[role] || [])] };
      const has = next[role].includes(key);
      if (on && !has) next[role].push(key);
      if (!on && has) next[role] = next[role].filter((k) => k !== key);
      next[role].sort();
      return next;
    });
  };

  const discard = () => {
    if (!payload) return;
    const cloned = {};
    Object.entries(payload.matrix || {}).forEach(([r, ks]) => { cloned[r] = [...ks]; });
    setDraft(cloned);
    toast.message("Discarded pending changes");
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/permissions", { matrix: draft });
      toast.success(`Permissions updated (${data.changes_applied} change${data.changes_applied === 1 ? "" : "s"}). Navigation refreshed.`);
      // Refresh current session's own perms + reload matrix + audit.
      await Promise.all([loadPermissions?.(), load()]);
    } catch (e) {
      toast.error(extractError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/permissions/reset");
      toast.success(`Reset applied (${data.changes_applied} cell${data.changes_applied === 1 ? "" : "s"} changed).`);
      await Promise.all([loadPermissions?.(), load()]);
    } catch (e) {
      toast.error(extractError(e, "Reset failed"));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) return null;
  if (!user || !hasPerm("admin.manage_permissions")) {
    return <Navigate to="/" replace />;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6 pb-24" data-testid="permissions-matrix-page">
        {/* Header */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-[#0F1E3D]/5 p-2 text-[#0F1E3D]">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Role Permissions</h1>
              <p className="text-sm text-slate-500">
                Last updated by{" "}
                <span className="font-medium text-slate-700">{payload?.updated_by || "—"}</span>{" "}
                at <span className="font-medium text-slate-700">{fmtTs(payload?.updated_at)}</span>
              </p>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={loading || saving}
                data-testid="reset-defaults-btn"
              >
                <RotateCcw className="mr-1.5 h-4 w-4" /> Reset to defaults
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="reset-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset to default permissions?</AlertDialogTitle>
                <AlertDialogDescription>
                  This restores the built-in role → permission mapping and discards
                  any custom changes. An audit entry will be emitted for every cell
                  that changes. This action can't be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={reset}
                  data-testid="reset-confirm-btn"
                >
                  Reset
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </header>

        {/* Info banner */}
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="scope-banner"
        >
          This matrix controls <span className="font-semibold">navigation visibility</span> only.
          Backend endpoint access remains role-based and is not yet editable here.
        </div>

        {/* Matrix */}
        <section className="rounded-md border border-slate-200 bg-white">
          {loading || !payload || !draft ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading matrix…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="permissions-matrix-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Permission</th>
                    {(payload.roles || []).map((r) => (
                      <th key={r} className="px-4 py-3 text-center font-semibold">
                        {ROLE_LABEL[r] || r}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedKeys).map(([groupName, keys]) => (
                    <PermGroup
                      key={groupName}
                      groupName={groupName}
                      keys={keys}
                      roles={payload.roles || []}
                      draft={draft}
                      lockedForAdmin={lockedForAdmin}
                      isOpen={openGroups[groupName] !== false}
                      onToggleGroup={() => setOpenGroups((g) => ({ ...g, [groupName]: g[groupName] === false }))}
                      onToggleCell={toggleCell}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Audit log */}
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Recent changes
          </h2>
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <table className="min-w-full text-sm" data-testid="permissions-audit-table">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-semibold">Time</th>
                  <th className="px-4 py-2 font-semibold">Changed by</th>
                  <th className="px-4 py-2 font-semibold">Role</th>
                  <th className="px-4 py-2 font-semibold">Permission</th>
                  <th className="px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                      No changes recorded yet.
                    </td>
                  </tr>
                ) : (
                  audit.map((e) => (
                    <tr
                      key={e.id}
                      className="border-b border-slate-100 last:border-0"
                      data-testid={`audit-row-${e.id}`}
                    >
                      <td className="whitespace-nowrap px-4 py-2 text-slate-600">{fmtTs(e.ts)}</td>
                      <td className="px-4 py-2 text-slate-700">{e.changed_by || "—"}</td>
                      <td className="px-4 py-2 text-slate-700">{ROLE_LABEL[e.role] || e.role}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-700">{e.perm_key}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            e.action === "granted"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-rose-100 text-rose-700"
                          }`}
                        >
                          {e.action}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Sticky save bar */}
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
            <div className="text-sm text-slate-600" data-testid="pending-changes-count">
              {pending === 0
                ? "No pending changes"
                : `${pending} change${pending === 1 ? "" : "s"} pending`}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={discard}
                disabled={pending === 0 || saving}
                data-testid="discard-changes-btn"
              >
                Discard
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={pending === 0 || saving}
                data-testid="save-permissions-btn"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

function PermGroup({
  groupName, keys, roles, draft, lockedForAdmin, isOpen, onToggleGroup, onToggleCell,
}) {
  const Chevron = isOpen ? ChevronDown : ChevronRight;
  return (
    <>
      <tr className="bg-slate-50/60" data-testid={`perm-group-${groupName.toLowerCase()}`}>
        <td
          colSpan={roles.length + 1}
          className="cursor-pointer px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100"
          onClick={onToggleGroup}
        >
          <div className="flex items-center gap-2">
            <Chevron className="h-3.5 w-3.5" />
            {groupName}
            <span className="text-slate-400 normal-case">({keys.length})</span>
          </div>
        </td>
      </tr>
      {isOpen && keys.map((k) => (
        <tr
          key={k.key}
          className="border-b border-slate-100 last:border-0"
          data-testid={`perm-row-${k.key}`}
        >
          <td className="px-4 py-2.5">
            <div className="font-medium text-slate-800">{k.label}</div>
            <div className="font-mono text-[11px] text-slate-400">{k.key}</div>
          </td>
          {roles.map((r) => {
            const isLocked = r === "admin" && lockedForAdmin.has(k.key);
            const checked = (draft[r] || []).includes(k.key);
            return (
              <td key={r} className="px-4 py-2.5 text-center">
                <div className="inline-flex items-center gap-1.5">
                  <Checkbox
                    data-testid={`cell-${r}-${k.key}`}
                    checked={checked}
                    disabled={isLocked}
                    onCheckedChange={(v) => onToggleCell(r, k.key, !!v)}
                  />
                  {isLocked && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="inline-flex text-slate-400"
                          data-testid={`lock-icon-${r}-${k.key}`}
                        >
                          <Lock className="h-3.5 w-3.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        Required for admin access. Cannot be revoked.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
